import { IRpgCharacter } from "../../models/IRpgCharacter";
import { IRpgStat } from "../../models/IRpgStat";
import { RpgRules } from "../../Services/RpgRulesService";

const CharStoragePrefix = "MyAdventureChar:";

export const delCharFromStorage = (charId: string | undefined) => {
    if (!charId) {
        return;
    }

    const key = `${CharStoragePrefix}${charId}`;
    sessionStorage.removeItem(key);
};

export const getCharFromStorage = (charId: string | undefined): IRpgCharacter => {
    let retChar: IRpgCharacter = RpgRules.newChar();

    if (charId) {
        const key = `${CharStoragePrefix}${charId}`;
        const tempCharacter = sessionStorage.getItem(key);
        if (tempCharacter) {
            try {
                retChar = JSON.parse(tempCharacter) as IRpgCharacter;
                RpgRules.calcArmorClass(retChar);
                RpgRules.calcLevel(retChar);
            } catch {
                // Ignore invalid drafts
            }
        }
    }

    return retChar;
};

export const saveCharToStorage = (rpgCharacter: IRpgCharacter | undefined) => {
    if (!rpgCharacter) {
        return;
    }

    const key = `${CharStoragePrefix}${rpgCharacter.Id}`;
    //sessionStorage.removeItem(key);
    //const tempCharacter = charList.find((char: IRpgCharacter) => char.Id === characterId);
    sessionStorage.setItem(key, JSON.stringify(rpgCharacter));
};

export function formatMod(mod: number): string {
    return mod >= 0 ? `+${mod}` : `${mod}`;
}

export function getAbilityDisplayName(name: string): string {
    const key = name.trim().toLowerCase();

    // supports "Str" / "STR" / "Strength" etc
    if (key.startsWith("str")) return "Strength";
    if (key.startsWith("dex")) return "Dexterity";
    if (key.startsWith("con")) return "Constitution";
    if (key.startsWith("int")) return "Intelligence";
    if (key.startsWith("wis")) return "Wisdom";
    if (key.startsWith("cha")) return "Charisma";

    // fallback
    return name;
}

export function getAbilityTotal(stat: { Value: number; RacialModifier?: number }): number {
    return (stat.Value ?? 0) + (stat.RacialModifier ?? 0);
}

export function calcAbilityModFromTotal(total: number): number {
    return Math.floor((total - 10) / 2);
}

export function getCoinQty(char: IRpgCharacter, coinName: string): number {
    const found = char.Items?.find(i => i.Name === coinName);
    return found?.Quantity ?? 0;
}

export function getCharacterPortrait(char: IRpgCharacter): string {
    const maleImgPath = "/images/Portraits/defaultMale.png";
    const femaleImgPath = "/images/Portraits/defaultFemale.png";

    if (char.Appearance?.PortraitUrl === undefined || char.Appearance?.PortraitUrl === null || char.Appearance?.PortraitUrl.trim() === "") {
        if (char.Appearance?.Gender === undefined || char.Appearance?.Gender === null) {
            return (Math.random() * 1000) < 500 ? femaleImgPath : maleImgPath;
        } else if (char.Appearance.Gender.toLowerCase() === "female") {
            return femaleImgPath;
        } else {
            return maleImgPath;
        }
    } else {
        return char.Appearance.PortraitUrl;
    }
}


// ------------------------------------------------------------
// Point Buy - D&D 5e rules based
// ------------------------------------------------------------
export const POINT_BUY_BUDGET = 27;

const POINT_BUY_COST_TABLE: Record<number, number> = {
    8: 0,
    9: 1,
    10: 2,
    11: 3,
    12: 4,
    13: 5,
    14: 7,
    15: 9,
    16: 11,
    17: 13,
    18: 15,
};

export function getPointBuyCost(score: number): number {
    let ret = POINT_BUY_COST_TABLE[score] ?? Number.MAX_SAFE_INTEGER;

    // This never hapopens, but lets handle it incase.
    if (ret < 0) {
        ret = -1;
    }
    // If score is not in the POINT_BUY_COST_TABLE we get a HUGE number instead.
    // Need to check score and see if it is above 15 or below 8 to know what math to do.
    if (ret > 20) {
        if (score < 8) {
            ret = 0 + (score - 8);
        } else {
            ret = 15 + (score - 18);
        }
    }

    return ret;
}

export function getTotalPointBuyCost(abilityScores: IRpgStat[]): number {
    let total = 0;

    for (const stat of abilityScores) {
        total += getPointBuyCost(stat.Value);
    }

    return total;
}

export function getRemainingPointBuyPoints(abilityScores: IRpgStat[]): number {
    const budget: number = POINT_BUY_BUDGET
    return budget - getTotalPointBuyCost(abilityScores);
}

export function canAdjustPointBuy(abilityScores: IRpgStat[], abilityName: string, delta: -1 | 1): boolean {
    const stat = abilityScores.find((s) => s.Name === abilityName);

    if (!stat) {
        return false;
    }

    const nextValue = stat.Value + delta;

    // D&D 5e bounds
    if (nextValue < 8 && delta === -1) {
        return false;
    }
    if (nextValue > 15 && delta === 1) {
        return false;
    }

    // Simulate cost change
    const currentCost = getPointBuyCost(stat.Value);
    const nextCost = getPointBuyCost(nextValue);

    const costDelta = nextCost - currentCost;
    const remaining = getRemainingPointBuyPoints(abilityScores);

    // Must not exceed budget
    if ((remaining - costDelta < 0) && delta === 1) {
        return false;
    }

    return true;
}
